import {get as _get, isEmpty as _isEmpty} from 'lodash';
import moment from 'moment';
import PropTypes from 'prop-types';

import {convertToInt} from '@utils/helpers';
import {getCompetitorsNameFromEvent} from '@utils/competitorName';
import {addHasMatchIdToStructure} from '@utils/eventObject';
import {
    INCIDENT_DURATION_UNIT_MINUTE,
    PROPERTY_LICENCE_TYPE_OTHER,
    PROPERTY_LICENCE_TYPE_SPORTRADAR,
} from '@constants/variables';
import {
    getEmptyReportData,
    isIncidentReport,
    parseReportDuration,
    getDistributionReportCoverage as getDistributionReportCoverageUtil,
    findReportsForDistribution as findReportsForDistributionUtil,
    formatReportScopeText,
    collectAllDistributions,
    getMatchDuration,
    parseCountAsDowntime,
} from '@utils/export/reportExportUtils';
import {convertSecondsToMinutesWithOneDecimal} from '@utils/momentWrapper';

function getReportData(report, eventDuration) {
    if (!report) {
        return getEmptyReportData();
    }

    const reportScope = formatReportScopeText(report, { includeVariantSuffix: true });

    return {
        reportType: _get(report, 'report_type.name', ''),
        reportScope,
        incidentSource: isIncidentReport(report) ? _get(report, 'incident_source.name', '') : '',
        incidentType: isIncidentReport(report) ? _get(report, 'incident_type.name', '') : '',
        incidentDuration: isIncidentReport(report) ? parseReportDuration(report, eventDuration) : '',
        countAsDowntime: isIncidentReport(report) ? parseCountAsDowntime(report.count_as_downtime) : '',
        comment: report.comment || '',
    };
}

export const reportedIncidentDuration = (report) => {
    if (!report?.incident_duration || !report?.incident_duration_unit) return '';

    if (INCIDENT_DURATION_UNIT_MINUTE === report.incident_duration_unit.id) {
        return `${convertSecondsToMinutesWithOneDecimal(report.incident_duration)}${report.incident_duration_unit.short_name}`;
    }

    return `${report.incident_duration}${report.incident_duration_unit.short_name}`;
};

export const exportExcel = (dataExport, propertyLicenceId, propertyLicenceTypeId) => {
    const productsWithUsage = 0 < dataExport.length
        ? dataExport[0].products_invoice_status.filter(p => p.has_usage)
        : [];

    const getBaseEventData = (event) => {
        const SPORTRADAR_MATCH_ID_INDEX = 1;
        const SPORTRADAR_CATEGORY_INDEX = 6;
        const OTHER_CATEGORY_INDEX = 5;

        event = addHasMatchIdToStructure(event);

        let data = [
            event.id,
            `${moment(event.start_datetime).format('YYYY-MM-DD')}`,
            `${moment(event.start_datetime).format('HH:mm')}`,
            `${event.end_datetime ? moment(event.end_datetime).format('YYYY-MM-DD') : ''}`,
            `${event.end_datetime ? moment(event.end_datetime).format('HH:mm') : ''}`,
            getCompetitorsNameFromEvent(event),
            _get(event, 'description', ''),
            _get(event, 'external_description', ''),
            _get(event, 'event_status.name', ''),
        ];

        if (PROPERTY_LICENCE_TYPE_SPORTRADAR === propertyLicenceTypeId) {
            data.splice(SPORTRADAR_MATCH_ID_INDEX, 0, _get(event, 'sr_event.match.id', ''));
            data.splice(SPORTRADAR_CATEGORY_INDEX, 0,
                _get(
                    event,
                    'sr_event.match.tournament.tournament_category.name',
                    _get(event, 'sr_event.tournament.tournament_category.name', '')
                ),
                _get(
                    event,
                    'sr_event.match.tournament.name',
                    _get(event, 'sr_event.tournament.name', '')
                )
            );
        } else if (PROPERTY_LICENCE_TYPE_OTHER === propertyLicenceTypeId) {
            data.splice(OTHER_CATEGORY_INDEX, 0,
                _get(event, 'av_event.content_category_level2.name', ''),
                _get(event, 'av_event.content_category_level3.name', '')
            );
        }

        return data;
    };

    const getDistributionData = (event, eventContent, product, distribution, report) => {
        const matchDuration = getMatchDuration(
            event.duration,
            event?.sr_event?.match?.started_datetime,
            event?.sr_event?.match?.ended_datetime
        );
        const reportData = getReportData(report, matchDuration);
        const invoiceStatuses = (() => {
            const statuses = productsWithUsage.map(p => {
                if (product && convertToInt(product.product_id) === convertToInt(p.product_id)) {
                    const productInvoiceStatus = event.products_invoice_status?.find(
                        pis => convertToInt(pis.product_id) === convertToInt(product.product_id)
                    );

                    return productInvoiceStatus?.invoice_status?.name;
                }
            }).filter(Boolean);

            return !_isEmpty(statuses) ? statuses : ['Undefined'];
        })();

        return [
            eventContent?.id || '',
            eventContent?.event_content_type?.name || '',
            eventContent?.event_content_variant?.short_name || '',
            eventContent?.name || '',
            eventContent?.is_main_content ? 'Yes' : 'No',
            distribution?.id || '',
            product?.short_name || '',
            distribution?.distribution_type?.name || '',
            distribution?.product_status?.name || '',
            distribution?.bookings ?? '',
            getDistributionReportCoverageUtil(event),
            reportData.reportType,
            reportData.reportScope,
            reportData.incidentSource,
            reportData.incidentType,
            reportData.incidentDuration,
            reportedIncidentDuration(report),
            reportData.countAsDowntime,
            matchDuration ? `${matchDuration?.toFixed(1)}min` : '',
            reportData.comment,
            invoiceStatuses,
        ];
    };

    const getDataRows = (event) => {
        const rows = [];
        const baseData = getBaseEventData(event);

        const allDistributions = collectAllDistributions(event.event_contents);

        if (0 === allDistributions.length) {
            const distributionData = getDistributionData(event, null, null, null, null);
            rows.push([...baseData, ...distributionData]);

            return rows;
        }

        const reports = event.reports || [];

        allDistributions.forEach(({ eventContent, product, distribution }) => {
            const distributionReports = distribution
                ? findReportsForDistributionUtil(reports, distribution.id)
                : [];

            if (0 === distributionReports.length) {
                const distributionData = getDistributionData(event, eventContent, product, distribution, null);
                rows.push([...baseData, ...distributionData]);
            } else {
                distributionReports.forEach(report => {
                    const distributionData = getDistributionData(event, eventContent, product, distribution, report);
                    rows.push([...baseData, ...distributionData]);
                });
            }
        });

        return rows;
    };

    const getColumns = () => {
        let columns = [
            'ID',
            'Start date',
            'Start time',
            'End date',
            'End time',
            'Competitors',
            'Description',
            'External description',
            'Status',
            'Event content ID',
            'Event content type',
            'Event content variant',
            'Event content name',
            'Is main content',
            'Distribution ID',
            'Product',
            'Distribution type',
            'Product status',
            'Bookings',
            'Report',
            'Report type',
            'Report scope',
            'Incident source',
            'Incident type',
            'Incident duration in %',
            'Reported incident duration',
            'Downtime',
            'Match duration',
            'Report comment',
            'Invoice status',
        ];

        if (PROPERTY_LICENCE_TYPE_SPORTRADAR === propertyLicenceTypeId) {
            columns.splice(1, 0, 'Match ID');
            columns.splice(6, 0, 'Category', 'Tournament');
        } else if (PROPERTY_LICENCE_TYPE_OTHER === propertyLicenceTypeId) {
            columns.splice(5, 0, '2nd level category', '3rd level category');
        }

        return columns;
    };

    return {
        dataExport: dataExport.flatMap(event => getDataRows(event)),
        filename: `property_licence_${propertyLicenceId}_events`,
        parseData: row => row,
        path: 'propertyLicences.events.index.path',
        titles: getColumns(),
    };
};

exportExcel.propTypes = {
    dataExport: PropTypes.array.isRequired,
};
